import { useCallback, useEffect, useState } from "react";
import fetchUserHistory from "../apis/fetchUserHistory";

interface DateFilterRange {
  label: string;
  startDate: string;
  endDate: string;
}

const dateToEpoch = (date: string, isEnd: boolean = false): number => {
  const [dd, mm, yyyy] = date.split("-");
  if (isEnd) {
    return Math.floor(new Date(`${yyyy}-${mm}-${dd}T23:59:59Z`).getTime() / 1000);
  } else {
    return Math.floor(new Date(`${yyyy}-${mm}-${dd}T00:00:00Z`).getTime() / 1000);
  }
};

const useUserDiagnosisHistory = (
  farmerId: number | null,
  dateFilter: DateFilterRange | null
) => {
  const pageSize = 10;

  const [diagnosisData, setDiagnosisData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isFilteredEmpty, setIsFilteredEmpty] = useState(false);

  const fetchHistoryData = useCallback(
    async (pageNum = 1, filter: DateFilterRange | null = dateFilter) => {
      setLoading(true);

      try {
        let start: number | undefined;
        let end: number | undefined;

        if (filter) {
          start = dateToEpoch(filter.startDate,false);
          end = dateToEpoch(filter.endDate,true);
        }

        const data = await fetchUserHistory(
          pageNum,
          pageSize,
          farmerId,
          start,
          end
        );

        if (data?.results?.length) {
          if (pageNum === 1) {
            setDiagnosisData(data.results);
          } else {
            setDiagnosisData((prev) => [...prev, ...data.results]);
          }

          setHasMore(Boolean(data.next));
          setPage(pageNum);
          setIsFilteredEmpty(false);
        } else {
          setDiagnosisData([]);
          setHasMore(false);
          setIsFilteredEmpty(!!filter);
        }
      } catch (error) {
        console.error("❌ Diagnosis history fetch failed:", error);
        setDiagnosisData([]);
        setHasMore(false);
        setIsFilteredEmpty(!!filter);
      } finally {
        setLoading(false);
      }
    },
    [farmerId, dateFilter]
  );

  const resetData = useCallback(() => {
    setDiagnosisData([]);
    setPage(1);
    setHasMore(false);
    setIsFilteredEmpty(false);
  }, []);

  useEffect(() => {
    resetData();
    fetchHistoryData(1, dateFilter);
  }, [dateFilter, resetData, fetchHistoryData]);

  return {
    diagnosisData,
    loading,
    hasMore,
    page,
    fetchNextPage: () => fetchHistoryData(page + 1),
    refreshData: () => {
      resetData();
      fetchHistoryData(1);
    },
    resetData,
    isFilteredEmpty,
  };
};

export default useUserDiagnosisHistory;
